/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/i18n.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useSyncExternalStore } from 'react';

export type Lang = 'de' | 'en';

const KEY = 'kontor-lang';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
};

function readStoredLang(): Lang {
  try {
    const val = localStorage.getItem(KEY);
    if (val === 'en' || val === 'de') return val;
  } catch { /* private mode */ }
  return 'de';
}

let currentLang: Lang = readStoredLang();

function applyLangAttributes(lang: Lang) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang === 'en' ? 'en' : 'de-CH';
    document.documentElement.dataset.lang = lang;
  }
}
applyLangAttributes(currentLang);

export function getLang(): Lang {
  return currentLang;
}

export function setLang(lang: Lang) {
  if (lang !== 'de' && lang !== 'en') return;
  currentLang = lang;
  try {
    localStorage.setItem(KEY, lang);
  } catch { /* private mode */ }
  applyLangAttributes(lang);
  emit();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kontor-lang', { detail: lang }));
  }
}

export const DICTIONARY: Record<Lang, Record<string, string>> = {
  de: {
    // Navigation Groups
    'nav.group.Arbeiten': 'Arbeiten',
    'nav.group.Buchhaltung': 'Buchhaltung',
    'nav.group.Debitoren & Kreditoren': 'Debitoren & Kreditoren',
    'nav.group.Compliance': 'Compliance',
    'nav.group.KI-Agenten': 'KI-Agenten',
    'nav.group.Add-ons': 'Add-ons',
    'nav.group.System': 'System',

    // Pages
    'nav.cockpit': 'Cockpit',
    'nav.insights': 'Analyse',
    'nav.agent': 'Kontor-Agent',
    'nav.review': 'Prüfen',
    'nav.questions': 'Rückfragen',
    'nav.practice': 'Leistungen',
    'nav.journal': 'Journal',
    'nav.documents': 'Belege',
    'nav.accounts': 'Konten',
    'nav.reports': 'Abschluss',
    'nav.close': 'Monatsabschluss',
    'nav.yearend': 'Jahresabschluss',
    'nav.wertschriften': 'Wertschriften',
    'nav.controlling': 'Controlling',
    'nav.invoices': 'Rechnungen',
    'nav.receivables': 'Offene Posten',
    'nav.payments': 'Zahlungen',
    'nav.vat': 'MWST',
    'nav.payroll': 'Lohn & AHV',
    'nav.bank': 'Bank',
    'nav.bankaccounts': 'Bankkonten',
    'nav.independence': 'Revisionsstelle',
    'nav.authorities': 'Behörden & Mandate',
    'nav.freehand-discovery': 'Freihand-Problemfinder (Fable vs. Codex)',
    'nav.accountant-training': 'Treuhand-Akademie (WARUM X vs. Y vs. Z)',
    'nav.triangular-tax': 'Dreiecks-Steueragent (STAF & IP-Box)',
    'nav.enterprise-monkey': 'Enterprise Monkey (9 Konzerne)',
    'nav.agent-office': 'Agenten-Büro (Live)',
    'nav.aiops': 'AIOps Command Center',
    'nav.finma': 'FINMA-Enforcement',
    'nav.payroll-qa': 'Odoo-QA',
    'nav.dlt': 'Agentic DLT',
    'nav.autonomous': '10x & 100x Suite',
    'nav.transmutation': 'Kapitalprüfung',
    'nav.millionx': 'Entwürfe und Screening',
    'nav.digital-twins': 'Digital Twins & Clarity',
    'nav.wow': 'WOW-Architektur',
    'nav.showcases': 'Showcases',
    'nav.auditsim': 'Revisionssimulation',
    'nav.cartridges': 'Länder-Module & Holding',
    'nav.audit': 'Prüfspur',
    'nav.integrity': 'Integrität',
    'nav.setup': 'Datenimport',
    'nav.setup_setup': 'Einrichtung',
    'nav.privacy': 'Datenschutz',
    'nav.settings': 'Einstellungen',
    'nav.admin': 'Verwaltung',
    'nav.account': 'Mein Konto',
    'nav.firm': 'Mandanten',
    'nav.all_tenants': 'Alle Mandanten',

    // Topbar & Shell
    'topbar.brand_tip': 'Kontor für OBT',
    'topbar.for_obt': 'für OBT',
    'topbar.search': 'Suchen oder fragen',
    'topbar.command': 'Command',
    'topbar.nav_toggle_collapse': 'Navigation einklappen',
    'topbar.nav_toggle_expand': 'Navigation ausklappen',
    'topbar.theme': 'Farbschema',
    'topbar.theme_light': 'Hell',
    'topbar.theme_dark': 'Dunkel',
    'topbar.theme_system': 'System',
    'topbar.scale': 'Schriftgrösse',
    'topbar.scale_font': 'Schrift',
    'topbar.tour': 'Tour starten (?)',
    'topbar.client_select': 'Mandant wählen…',
    'topbar.client_search': 'Mandant suchen…',
    'topbar.client_switch': 'Mandant wechseln',
    'topbar.offline_agent': 'Offline-Agent (ohne API-Key)',
    'topbar.offline_agent_tip': 'ANTHROPIC_API_KEY setzen, um Claude zu aktivieren',
    'topbar.claude_tip': 'Claude mit Werkzeugen, Auditor und Policy',

    // User Menu
    'user.my_account': 'Mein Konto',
    'user.admin': 'Verwaltung',
    'user.logout': 'Abmelden',

    // Language Chooser
    'lang.label': 'Sprache',
    'lang.de': 'Deutsch',
    'lang.en': 'English',
    'lang.switch_de': 'Sprache: Deutsch (DE)',
    'lang.switch_en': 'Language: English (EN)',

    // Banners
    'banner.demo': 'Demo-Mandant. Fiktive Firma mit Testdaten zum Ausprobieren; echte Mandanten sind davon getrennt.',
    'banner.demo_manage': 'Demo-Modus verwalten',
    'banner.setup': 'In Einrichtung. Die Daten werden übernommen; erst nach dem Aktivieren ist der Mandant produktiv.',
    'banner.setup_btn': 'Zur Einrichtung',

    // Tour
    'tour.skip': 'Tour überspringen',
    'tour.back': 'Zurück',
    'tour.next': 'Weiter',
    'tour.done': 'Los geht’s',
    'tour.step': 'SCHRITT {i}/{n}',
    'tour.step1.title': 'Dein KI-Buchhalter pro Mandant',
    'tour.step1.body': 'Kontor erledigt die Buchhaltung, du prüfst nur die Ausnahmen. Sechs Schritte, eine Minute.',
    'tour.step2.title': 'Schick einfach alles',
    'tour.step2.body': 'Quittungen, Rechnungen, camt.053-Auszüge oder eine Frage: per Drag and Drop, E-Mail an die Belegadresse oder hier im Chat. Kontor erkennt selbst, was es ist.',
    'tour.step3.title': 'Jeder Agentenschritt ist sichtbar',
    'tour.step3.body': 'Die Demo-Belege laufen durch Eingang, Buchhalter, Auditor und Policy. Du siehst live, was automatisch gebucht wird und warum der Rest auf dich wartet.',
    'tour.step4.title': 'Du prüfst nur Ausnahmen',
    'tour.step4.body': 'Neue Lieferanten, grosse Beträge und Auditor-Befunde landen hier. Jede Korrektur wird zur Regel: der nächste Beleg desselben Lieferanten bucht sich selbst.',
    'tour.step5.title': 'Compliance als Nebenprodukt',
    'tour.step5.body': 'MWST nach ESTV-Ziffern mit eCH-0217-Export, AHV-Lohnbescheinigung und Bilanz nach OR 959a entstehen direkt aus dem Journal, mit Abstimmungen.',
    'tour.step6.title': 'Revisionssicher nach GeBüV',
    'tour.step6.body': 'Journal und Prüfspur sind unveränderlich und hash-verkettet. Jede Entscheidung von Agent und Mensch ist nachvollziehbar. Mit ? startest du diese Tour erneut.',

    // Command Palette Groups & Actions
    'cmd.group.jump': 'Springen',
    'cmd.group.switch_client': 'Mandant wechseln',
    'cmd.group.actions': 'Aktionen',
    'cmd.group.command': 'Command',
    'cmd.group.help': 'Hilfe',
    'cmd.group.appearance': 'Darstellung',
    'cmd.client_overview': 'Mandantenübersicht',
    'cmd.upload_receipts': 'Belege hochladen',
    'cmd.demo_process': 'Demo-Belege verarbeiten lassen',
    'cmd.import_data': 'Daten importieren (Kontenplan, Eröffnungsbilanz, Buchungen …)',
    'cmd.admin': 'Verwaltung: Personen, Demo-Modus, Sicherheit',
    'cmd.account': 'Mein Konto: Passwort, Zwei-Faktor',
    'cmd.bank_rec': 'Bankabstimmung starten',
    'cmd.health_analysis': 'Geschäftslage analysieren',
    'cmd.finma': 'FINMA-Vorabklärung: Bewilligungspflicht prüfen',
    'cmd.payroll_qa': 'Odoo-QA: Lohn mit Odoo vergleichen',
    'cmd.close_prep': 'Monatsabschluss vorbereiten',
    'cmd.vat_return': 'MWST-Abrechnung dieses Quartal',
    'cmd.payroll_run': 'Lohnlauf vorbereiten',
    'cmd.open_command': 'Command öffnen',
    'cmd.start_tour': 'Tour starten',
    'cmd.theme_light': 'Design: Hell',
    'cmd.theme_dark': 'Design: Dunkel',
    'cmd.theme_system': 'Design: System',
    'cmd.font_scale': 'Schriftgrösse wechseln (aA)',
    'cmd.nav_collapse': 'Navigation einklappen',
    'cmd.nav_expand': 'Navigation ausklappen',

    // Common UI terms
    'ui.save': 'Speichern',
    'ui.cancel': 'Abbrechen',
    'ui.delete': 'Löschen',
    'ui.close': 'Schliessen',
    'ui.back': 'Zurück',
    'ui.next': 'Weiter',
    'ui.edit': 'Bearbeiten',
    'ui.new': 'Neu',
    'ui.search': 'Suchen',
    'ui.filter': 'Filtern',
    'ui.export': 'Exportieren',
    'ui.retry': 'Erneut versuchen',
    'ui.loading': 'Wird geladen…',
    'ui.empty': 'Noch keine Daten',
    'ui.confidence': 'Konfidenz des Agenten',
    'ui.just_now': 'gerade eben',
    'ui.min_ago': 'vor {n} Min.',
    'ui.hours_ago': 'vor {n} Std.',
    'ui.all_done': 'Alles erledigt',
    'ui.open': 'offen',

    // Cockpit
    'cockpit.liquidity': 'Liquidität',
    'cockpit.liquidity_hint': 'Kasse und Bank per heute',
    'cockpit.result': 'Ergebnis {year}',
    'cockpit.result_hint': 'Ertrag {rev} · Aufwand {exp}',
    'cockpit.vat': 'MWST {period}',
    'cockpit.vat_filed': 'eingereicht',
    'cockpit.vat_filed_unsubmitted': 'abgerechnet, nicht eingereicht',
    'cockpit.vat_payable': 'Zahllast',
    'cockpit.vat_credit': 'Guthaben',
    'cockpit.vat_due': 'fällig {date}',
    'cockpit.open_receivables': 'Offene Debitoren',
    'cockpit.receivables_overdue': '{n} überfällig ({amount})',
    'cockpit.receivables_none_overdue': '{n} Rechnungen, keine überfällig',
    'cockpit.open_payables': 'Offene Kreditoren',
    'cockpit.payables_for_payment': '{n} Rechnungen zur Zahlung',
    'cockpit.automation': 'Automatisierung',
    'cockpit.automation_hint': '{auto} von {decided} Buchungen ohne Eingriff',
    'cockpit.open_balance': 'Bilanz öffnen',
    'cockpit.ask_agent': 'Kontor fragen',
    'cockpit.monthly_chart': 'Ertrag und Aufwand pro Monat',
    'cockpit.monthly_sub': '{year}, in CHF',
    'cockpit.waiting_for_you': 'Wartet auf dich',
    'cockpit.proposals': 'Buchungsvorschläge',
    'cockpit.proposals_hint': 'Neue Muster, grosse Beträge, Auditor-Befunde',
    'cockpit.client_questions': 'Rückfragen an den Kunden',
    'cockpit.client_questions_hint': 'Der Agent braucht eine fachliche Antwort',
    'cockpit.unmatched_bank': 'Offene Banktransaktionen',
    'cockpit.unmatched_bank_hint': 'Noch nicht zugeordnet oder gebucht',
    'cockpit.deadlines': 'Fristen',
    'cockpit.deadlines_sub': 'MWST, AHV, Abschluss',
    'cockpit.overdue_prefix': 'überfällig · ',
    'cockpit.recent_decisions': 'Letzte Agentenentscheide',
    'cockpit.journal_link': 'Journal',
    'cockpit.col_receipt': 'Beleg',
    'cockpit.col_amount': 'Betrag',
    'cockpit.col_confidence': 'Konfidenz',
    'cockpit.col_status': 'Status',
    'cockpit.col_when': 'Wann',
    'cockpit.as_of': 'Stand {date}',
    'cockpit.vat_effective': 'effektive MWST-Methode',
    'cockpit.vat_saldo': 'Saldosteuersatzmethode',
    'cockpit.lock_until': 'Buchungssperre bis {date}',

    // Firm Overview
    'firm.badge': 'Agentische Buchhaltung für Treuhand-Mandate',
    'firm.headline': 'Die Buchhaltung erledigt sich. Du prüfst die Ausnahmen.',
    'firm.body': 'Kontor führt für jeden Mandanten ein vollständiges, revisionssicheres Hauptbuch nach Schweizer Recht. Agenten lesen Belege, kontieren, stimmen die Bank ab und bereiten MWST und AHV vor. Du gibst frei, was Urteilsvermögen braucht.',
    'firm.pipe_receipt': 'Beleg, Frage oder Auszug',
    'firm.pipe_intake': 'Eingang und Buchhalter',
    'firm.pipe_auditor': 'Auditor und Policy',
    'firm.pipe_approval': 'Freigabe, falls nötig',
    'firm.pipe_journal': 'Hash-verkettetes Journal',
    'firm.pipe_statements': 'Bilanz, MWST, AHV',
    'firm.kpi_clients': 'Mandanten',
    'firm.kpi_demo_count': 'davon {n} Demo',
    'firm.kpi_in_portfolio': 'im Portfolio',
    'firm.kpi_waiting': 'Warten auf dich',
    'firm.kpi_waiting_hint': 'Vorschläge und Rückfragen, alle Mandanten',
    'firm.kpi_attention': 'Handlungsbedarf',
    'firm.kpi_attention_hint_bad': 'Mandanten unter «Solide» in der Geschäftsgesundheit',
    'firm.kpi_attention_hint_good': 'alle Mandanten solide oder besser',
    'firm.kpi_auto': 'Automatisch verbucht',
    'firm.kpi_auto_hint': 'Anteil ohne menschlichen Eingriff',
    'firm.kpi_next_deadline': 'Nächste Frist',
    'firm.kpi_overdue_deadline': 'Überfällige Frist',
    'firm.kpi_no_deadlines': 'keine offenen Fristen',
    'firm.clients_title': 'Mandanten',
    'firm.clients_sub': 'Geschäftsgesundheit, Liquidität und Ergebnis per heute, dazu was auf dich wartet.',
    'firm.new_client': 'Neuer Mandant',
    'firm.stat_liquidity': 'Liquidität',
    'firm.stat_result': 'Ergebnis lfd. Jahr',
    'firm.stat_review': 'Zur Prüfung',
    'firm.level_suggest': 'Vorschlagen',
    'firm.level_assisted': 'Assistiert',
    'firm.level_auto': 'Autonom',

    // Command Panel
    'cmd.new_instruction': 'Neuer Auftrag',
    'cmd.tour': 'Tour starten',
    'cmd.close': 'Schliessen (Esc oder ⌘J)',
    'cmd.no_tenant_title': 'Für welchen Mandanten?',
    'cmd.no_tenant_body': 'Command arbeitet immer in genau einem Mandanten. Wähle einen, dann geht es weiter.',
    'cmd.no_tenant_selected': 'Kein Mandant gewählt',
    'cmd.empty_title': 'Was soll erledigt werden?',
    'cmd.empty_body': 'Ein Satz genügt. Command entwirft Rechnung, Buchung oder Freigabe mit allen Zahlen. Du prüfst und führst aus.',
    'cmd.suggestions_for': 'Vorschläge für {page}',
    'cmd.placeholder_active': 'Was soll erledigt werden? / für Befehle',
    'cmd.placeholder_inactive': 'Zuerst einen Mandanten wählen',
    'cmd.btn_commands': 'Befehle',
    'cmd.btn_commands_tip': 'Alle Befehle',
    'cmd.hint_exec': 'ausführen',
    'cmd.hint_send': 'senden',
    'cmd.hint_prev': 'letzter Auftrag',
    'cmd.stop': 'Stopp',
    'cmd.send': 'Senden',
    'cmd.trust': 'Erst Entwurf, dann Ausführen. Ohne deine Bestätigung ändert sich nichts.',
    'cmd.steps': '{n} Schritte',
    'cmd.step_single': '1 Schritt',
    'cmd.working': 'Command arbeitet',
    'cmd.resend': 'Erneut senden',
    'cmd.conn_interrupted': 'Verbindung unterbrochen',
    'cmd.aborted': 'Abgebrochen.',
    'cmd.executed': 'Ausgeführt.',
    'cmd.stale': 'Entwurf veraltet: die Daten haben sich inzwischen geändert.',
    'cmd.failed': 'Ausführung fehlgeschlagen',
    'cmd.remove_context': 'Bezug entfernen',
    'cmd.context_tip': 'Command bezieht sich auf diese Seite und Auswahl',
    'cmd.slash_empty': 'Kein Befehl passt. Enter schickt den Text als Auftrag.',
    'cmd.slash_aria': 'Befehle',
    'cmd.aria_input': 'Auftrag an Command',

    // Review
    'review.title': 'Prüfen',
    'review.subtitle': 'Nur was die Policy nicht selbst buchen darf: neue Muster, Beträge über der Vier-Augen-Grenze und Auditor-Befunde. Jede Korrektur wird zur Kontierungsregel.',
    'review.shortcut_nav': 'nächster / vorheriger',
    'review.shortcut_approve': 'freigeben',
    'review.shortcut_reject': 'ablehnen',
    'review.shortcut_edit': 'Betrag bearbeiten',
    'review.all_done_title': 'Alles geprüft',
    'review.all_done_body': 'Keine offenen Vorschläge. Der Agent meldet sich, sobald etwas dein Urteil braucht.',
    'review.new_receipts_btn': 'Neue Belege senden',
    'review.queue_title': 'Warteschlange',
    'review.approve_btn': 'Freigeben',
    'review.approve_corrected_btn': 'Korrigiert freigeben',
    'review.reject_btn': 'Ablehnen',
    'review.approve_as': 'Freigeben als',
    'review.already_approved': 'Du hast schon freigegeben. Zweite Freigabe als',
    'review.learning_hint': 'Deine Änderung wird als Kontierungsregel gelernt.',
    'review.entry_text': 'Buchungstext',
    'review.col_account': 'Konto',
    'review.col_side': 'Seite',
    'review.col_amount': 'Betrag brutto',
    'review.col_vat': 'MWST-Code',
    'review.add_line': 'Zeile',
    'review.computed_by_kontor': 'Von Kontor berechnet, nicht änderbar',
    'review.debit': 'Soll',
    'review.credit': 'Haben',
    'review.diff': 'Differenz',
    'review.original': 'Original',

    // Invoices
    'invoices.title': 'Rechnungen',
    'invoices.subtitle': 'Rechnungen mit allen Angaben nach Art. 26 MWSTG und QR-Zahlteil. Beim Ausstellen erhält die Rechnung ihre Nummer, wird gebucht, als Beleg archiviert und als offener Posten geführt; danach ist sie unveränderlich. Korrekturen laufen über eine Gutschrift.',
    'invoices.tab_invoices': 'Rechnungen',
    'invoices.tab_credit_notes': 'Gutschriften',
    'invoices.tab_quotes': 'Offerten',
    'invoices.tab_templates': 'Vorlagen',
    'invoices.tab_contacts': 'Kontakte',
  },
  en: {
    // Navigation Groups
    'nav.group.Arbeiten': 'Operations',
    'nav.group.Buchhaltung': 'Accounting',
    'nav.group.Debitoren & Kreditoren': 'AR & AP',
    'nav.group.Compliance': 'Compliance',
    'nav.group.KI-Agenten': 'AI Agents',
    'nav.group.Add-ons': 'Add-ons',
    'nav.group.System': 'System',

    // Pages
    'nav.cockpit': 'Cockpit',
    'nav.insights': 'Analytics',
    'nav.agent': 'Kontor Agent',
    'nav.review': 'Review',
    'nav.questions': 'Inquiries',
    'nav.practice': 'Services',
    'nav.journal': 'Journal',
    'nav.documents': 'Documents',
    'nav.accounts': 'Chart of Accounts',
    'nav.reports': 'Financial Statements',
    'nav.close': 'Month-End Close',
    'nav.yearend': 'Year-End Close',
    'nav.wertschriften': 'Securities & Valuation',
    'nav.controlling': 'Controlling',
    'nav.invoices': 'Invoices',
    'nav.receivables': 'Open Items',
    'nav.payments': 'Payments',
    'nav.vat': 'VAT',
    'nav.payroll': 'Payroll & AHV',
    'nav.bank': 'Bank',
    'nav.bankaccounts': 'Bank Accounts',
    'nav.independence': 'Audit Independence',
    'nav.authorities': 'Authorities & Mandates',
    'nav.freehand-discovery': 'Freehand Problem Discovery (Fable vs. Codex)',
    'nav.accountant-training': 'Fiduciary Training Academy (WHY X vs. Y vs. Z)',
    'nav.triangular-tax': 'Triangular Tax & IP Box Agent',
    'nav.enterprise-monkey': 'Enterprise Monkey (9 Mega-Corps)',
    'nav.agent-office': 'Agent Office (Live)',
    'nav.aiops': 'AIOps Command Center',
    'nav.finma': 'FINMA Enforcement',
    'nav.payroll-qa': 'Odoo QA',
    'nav.dlt': 'Agentic DLT',
    'nav.autonomous': '10x & 100x Suite',
    'nav.transmutation': 'Capital Check',
    'nav.millionx': 'Drafts & Screening',
    'nav.digital-twins': 'Digital Twins & Clarity',
    'nav.wow': 'WOW Architecture',
    'nav.showcases': 'Showcases',
    'nav.auditsim': 'Audit Simulation',
    'nav.cartridges': 'Country Cartridges & Holding',
    'nav.audit': 'Audit Trail',
    'nav.integrity': 'Integrity',
    'nav.setup': 'Data Import',
    'nav.setup_setup': 'Setup',
    'nav.privacy': 'Privacy',
    'nav.settings': 'Settings',
    'nav.admin': 'Administration',
    'nav.account': 'My Account',
    'nav.firm': 'Clients',
    'nav.all_tenants': 'All Clients',

    // Topbar & Shell
    'topbar.brand_tip': 'Kontor for OBT',
    'topbar.for_obt': 'for OBT',
    'topbar.search': 'Search or ask',
    'topbar.command': 'Command',
    'topbar.nav_toggle_collapse': 'Collapse navigation',
    'topbar.nav_toggle_expand': 'Expand navigation',
    'topbar.theme': 'Color theme',
    'topbar.theme_light': 'Light',
    'topbar.theme_dark': 'Dark',
    'topbar.theme_system': 'System',
    'topbar.scale': 'Font size',
    'topbar.scale_font': 'Font',
    'topbar.tour': 'Start tour (?)',
    'topbar.client_select': 'Select client…',
    'topbar.client_search': 'Search client…',
    'topbar.client_switch': 'Switch client',
    'topbar.offline_agent': 'Offline Agent (no API key)',
    'topbar.offline_agent_tip': 'Set ANTHROPIC_API_KEY to activate Claude',
    'topbar.claude_tip': 'Claude with tools, auditor and policy',

    // User Menu
    'user.my_account': 'My Account',
    'user.admin': 'Administration',
    'user.logout': 'Sign out',

    // Language Chooser
    'lang.label': 'Language',
    'lang.de': 'Deutsch',
    'lang.en': 'English',
    'lang.switch_de': 'Sprache: Deutsch (DE)',
    'lang.switch_en': 'Language: English (EN)',

    // Banners
    'banner.demo': 'Demo client. Fictional company with test data for exploration; real clients are kept separate.',
    'banner.demo_manage': 'Manage demo mode',
    'banner.setup': 'In setup. Data is being imported; client becomes live only after activation.',
    'banner.setup_btn': 'Go to setup',

    // Tour
    'tour.skip': 'Skip tour',
    'tour.back': 'Back',
    'tour.next': 'Next',
    'tour.done': 'Get started',
    'tour.step': 'STEP {i}/{n}',
    'tour.step1.title': 'Your AI bookkeeper per client',
    'tour.step1.body': 'Kontor handles the accounting, you only review exceptions. Six steps, one minute.',
    'tour.step2.title': 'Send anything',
    'tour.step2.body': 'Receipts, invoices, camt.053 statements or a question: via drag & drop, email to the document inbox or directly in chat. Kontor detects what it is.',
    'tour.step3.title': 'Every agent step is visible',
    'tour.step3.body': 'Demo documents pass through intake, bookkeeper, auditor, and policy. Watch live what gets posted automatically and why the rest awaits review.',
    'tour.step4.title': 'You only review exceptions',
    'tour.step4.body': 'New vendors, large amounts, and auditor findings arrive here. Every correction becomes a rule: the next receipt from the same vendor books automatically.',
    'tour.step5.title': 'Compliance as a byproduct',
    'tour.step5.body': 'VAT returns by ESTV boxes with eCH-0217 export, social security certificates, and balance sheets per Swiss CO 959a are generated straight from the journal.',
    'tour.step6.title': 'Audit-proof under Swiss GeBüV',
    'tour.step6.body': 'Journal and audit trail are immutable and hash-chained. Every decision by agent or human is fully traceable. Press ? to restart this tour.',

    // Command Palette Groups & Actions
    'cmd.group.jump': 'Navigate',
    'cmd.group.switch_client': 'Switch Client',
    'cmd.group.actions': 'Actions',
    'cmd.group.command': 'Command',
    'cmd.group.help': 'Help',
    'cmd.group.appearance': 'Appearance',
    'cmd.client_overview': 'Client Overview',
    'cmd.upload_receipts': 'Upload documents',
    'cmd.demo_process': 'Process demo documents',
    'cmd.import_data': 'Import data (chart of accounts, opening balance, entries…)',
    'cmd.admin': 'Administration: Users, demo mode, security',
    'cmd.account': 'My Account: Password, two-factor',
    'cmd.bank_rec': 'Start bank reconciliation',
    'cmd.health_analysis': 'Analyze business health',
    'cmd.finma': 'FINMA check: verify licensing requirements',
    'cmd.payroll_qa': 'Odoo QA: Compare payroll with Odoo',
    'cmd.close_prep': 'Prepare month-end close',
    'cmd.vat_return': 'VAT return for this quarter',
    'cmd.payroll_run': 'Prepare payroll run',
    'cmd.open_command': 'Open Command',
    'cmd.start_tour': 'Start tour',
    'cmd.theme_light': 'Theme: Light',
    'cmd.theme_dark': 'Theme: Dark',
    'cmd.theme_system': 'Theme: System',
    'cmd.font_scale': 'Change font size (aA)',
    'cmd.nav_collapse': 'Collapse navigation',
    'cmd.nav_expand': 'Expand navigation',

    // Common UI terms
    'ui.save': 'Save',
    'ui.cancel': 'Cancel',
    'ui.delete': 'Delete',
    'ui.close': 'Close',
    'ui.back': 'Back',
    'ui.next': 'Next',
    'ui.edit': 'Edit',
    'ui.new': 'New',
    'ui.search': 'Search',
    'ui.filter': 'Filter',
    'ui.export': 'Export',
    'ui.retry': 'Try again',
    'ui.loading': 'Loading…',
    'ui.empty': 'No data yet',
    'ui.confidence': 'Agent confidence',
    'ui.just_now': 'just now',
    'ui.min_ago': '{n} min ago',
    'ui.hours_ago': '{n} h ago',
    'ui.all_done': 'All done',
    'ui.open': 'open',

    // Cockpit
    'cockpit.liquidity': 'Liquidity',
    'cockpit.liquidity_hint': 'Cash and bank as of today',
    'cockpit.result': 'Result {year}',
    'cockpit.result_hint': 'Revenue {rev} · Expense {exp}',
    'cockpit.vat': 'VAT {period}',
    'cockpit.vat_filed': 'filed',
    'cockpit.vat_filed_unsubmitted': 'settled, not submitted',
    'cockpit.vat_payable': 'Tax payable',
    'cockpit.vat_credit': 'Credit',
    'cockpit.vat_due': 'due {date}',
    'cockpit.open_receivables': 'Open Receivables',
    'cockpit.receivables_overdue': '{n} overdue ({amount})',
    'cockpit.receivables_none_overdue': '{n} invoices, none overdue',
    'cockpit.open_payables': 'Open Payables',
    'cockpit.payables_for_payment': '{n} invoices for payment',
    'cockpit.automation': 'Automation',
    'cockpit.automation_hint': '{auto} of {decided} entries without human intervention',
    'cockpit.open_balance': 'Open Balance Sheet',
    'cockpit.ask_agent': 'Ask Kontor',
    'cockpit.monthly_chart': 'Monthly Revenue and Expense',
    'cockpit.monthly_sub': '{year}, in CHF',
    'cockpit.waiting_for_you': 'Waiting for you',
    'cockpit.proposals': 'Booking Proposals',
    'cockpit.proposals_hint': 'New patterns, large amounts, auditor findings',
    'cockpit.client_questions': 'Client Inquiries',
    'cockpit.client_questions_hint': 'The agent needs a subject-matter answer',
    'cockpit.unmatched_bank': 'Open Bank Transactions',
    'cockpit.unmatched_bank_hint': 'Not yet matched or booked',
    'cockpit.deadlines': 'Deadlines',
    'cockpit.deadlines_sub': 'VAT, AHV, closing',
    'cockpit.overdue_prefix': 'overdue · ',
    'cockpit.recent_decisions': 'Recent Agent Decisions',
    'cockpit.journal_link': 'Journal',
    'cockpit.col_receipt': 'Document',
    'cockpit.col_amount': 'Amount',
    'cockpit.col_confidence': 'Confidence',
    'cockpit.col_status': 'Status',
    'cockpit.col_when': 'When',
    'cockpit.as_of': 'As of {date}',
    'cockpit.vat_effective': 'effective VAT method',
    'cockpit.vat_saldo': 'flat tax rate method',
    'cockpit.lock_until': 'Posting lock until {date}',

    // Firm Overview
    'firm.badge': 'Agentic Accounting for Fiduciary Mandates',
    'firm.headline': 'Accounting keeps itself. You review the exceptions.',
    'firm.body': 'Kontor maintains a complete, audit-compliant general ledger under Swiss law for every client. Agents ingest receipts, assign accounts, reconcile bank feeds, and prepare VAT and AHV. You approve what requires judgment.',
    'firm.pipe_receipt': 'Document, inquiry or statement',
    'firm.pipe_intake': 'Intake & Bookkeeper',
    'firm.pipe_auditor': 'Auditor & Policy',
    'firm.pipe_approval': 'Approval if needed',
    'firm.pipe_journal': 'Hash-chained Journal',
    'firm.pipe_statements': 'Balance sheet, VAT, AHV',
    'firm.kpi_clients': 'Clients',
    'firm.kpi_demo_count': 'of which {n} demo',
    'firm.kpi_in_portfolio': 'in portfolio',
    'firm.kpi_waiting': 'Waiting for you',
    'firm.kpi_waiting_hint': 'Proposals and inquiries, all clients',
    'firm.kpi_attention': 'Action Required',
    'firm.kpi_attention_hint_bad': 'Clients below "Solid" in business health',
    'firm.kpi_attention_hint_good': 'all clients solid or better',
    'firm.kpi_auto': 'Auto-posted',
    'firm.kpi_auto_hint': 'Share without human intervention',
    'firm.kpi_next_deadline': 'Next Deadline',
    'firm.kpi_overdue_deadline': 'Overdue Deadline',
    'firm.kpi_no_deadlines': 'no open deadlines',
    'firm.clients_title': 'Clients',
    'firm.clients_sub': 'Business health, liquidity and result as of today, plus what awaits you.',
    'firm.new_client': 'New Client',
    'firm.stat_liquidity': 'Liquidity',
    'firm.stat_result': 'YTD Result',
    'firm.stat_review': 'To review',
    'firm.level_suggest': 'Suggest',
    'firm.level_assisted': 'Assisted',
    'firm.level_auto': 'Autonomous',

    // Command Panel
    'cmd.new_instruction': 'New instruction',
    'cmd.tour': 'Start tour',
    'cmd.close': 'Close (Esc or ⌘J)',
    'cmd.no_tenant_title': 'Which client?',
    'cmd.no_tenant_body': 'Command always works within a single client. Select one to continue.',
    'cmd.no_tenant_selected': 'No client selected',
    'cmd.empty_title': 'What needs to be done?',
    'cmd.empty_body': 'One sentence is enough. Command drafts invoices, entries, or approvals with all figures. You verify and execute.',
    'cmd.suggestions_for': 'Suggestions for {page}',
    'cmd.placeholder_active': 'What needs to be done? / for commands',
    'cmd.placeholder_inactive': 'Select a client first',
    'cmd.btn_commands': 'Commands',
    'cmd.btn_commands_tip': 'All commands',
    'cmd.hint_exec': 'execute',
    'cmd.hint_send': 'send',
    'cmd.hint_prev': 'previous instruction',
    'cmd.stop': 'Stop',
    'cmd.send': 'Send',
    'cmd.trust': 'Draft first, then execute. Nothing changes without your confirmation.',
    'cmd.steps': '{n} steps',
    'cmd.step_single': '1 step',
    'cmd.working': 'Command is working',
    'cmd.resend': 'Send again',
    'cmd.conn_interrupted': 'Connection interrupted',
    'cmd.aborted': 'Aborted.',
    'cmd.executed': 'Executed.',
    'cmd.stale': 'Draft stale: data has changed in the meantime.',
    'cmd.failed': 'Execution failed',
    'cmd.remove_context': 'Remove context',
    'cmd.context_tip': 'Command refers to this page and selection',
    'cmd.slash_empty': 'No matching command. Enter sends the text as an instruction.',
    'cmd.slash_aria': 'Commands',
    'cmd.aria_input': 'Instruction for Command',

    // Review
    'review.title': 'Review',
    'review.subtitle': 'Only what policy may not book automatically: new patterns, amounts above the four-eyes limit, and auditor findings. Every correction becomes an account rule.',
    'review.shortcut_nav': 'next / previous',
    'review.shortcut_approve': 'approve',
    'review.shortcut_reject': 'reject',
    'review.shortcut_edit': 'edit amount',
    'review.all_done_title': 'All reviewed',
    'review.all_done_body': 'No open proposals. The agent will notify you as soon as something needs your judgment.',
    'review.new_receipts_btn': 'Send new receipts',
    'review.queue_title': 'Queue',
    'review.approve_btn': 'Approve',
    'review.approve_corrected_btn': 'Approve corrected',
    'review.reject_btn': 'Reject',
    'review.approve_as': 'Approve as',
    'review.already_approved': 'You have already approved. Second approval as',
    'review.learning_hint': 'Your correction will be learned as an account rule.',
    'review.entry_text': 'Posting text',
    'review.col_account': 'Account',
    'review.col_side': 'Side',
    'review.col_amount': 'Gross amount',
    'review.col_vat': 'VAT code',
    'review.add_line': 'Line',
    'review.computed_by_kontor': 'Computed by Kontor, not editable',
    'review.debit': 'Debit',
    'review.credit': 'Credit',
    'review.diff': 'Difference',
    'review.original': 'Original',

    // Invoices
    'invoices.title': 'Invoices',
    'invoices.subtitle': 'Invoices with all details per Art. 26 MWSTG and Swiss QR bill. Upon issuance, the invoice receives its sequential number, is posted, archived as a document, and tracked as an open item; thereafter it is immutable. Corrections are made via credit note.',
    'invoices.tab_invoices': 'Invoices',
    'invoices.tab_credit_notes': 'Credit Notes',
    'invoices.tab_quotes': 'Quotes',
    'invoices.tab_templates': 'Templates',
    'invoices.tab_contacts': 'Contacts',
  },
};

export function t(key: string, params?: Record<string, string | number>): string {
  const dict = DICTIONARY[currentLang] ?? DICTIONARY.de;
  let text = dict[key] ?? DICTIONARY.de[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return text;
}

export function useLang() {
  const lang = useSyncExternalStore(subscribe, getLang);
  const boundT = (key: string, params?: Record<string, string | number>) => {
    const dict = DICTIONARY[lang] ?? DICTIONARY.de;
    let text = dict[key] ?? DICTIONARY.de[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return text;
  };
  return { lang, setLang, t: boundT };
}

export function useT() {
  const { t } = useLang();
  return t;
}
